import { shopifyAPI } from "../api";
import { formatMoney } from "./g-utils";

class ProductSection extends HTMLElement {
  constructor() {
    super();

    this.productForm = this.querySelector(".js-product-form");
    this.optionRadios = this.productForm.querySelectorAll(".js-option-radio");
  }

  connectedCallback() {
    this._initStickyAside();
    this._attachEventListeners();
  }

  _initStickyAside() {
    const stickyElement = this.querySelector(".js-left");
    const header = document.querySelector("#shopify-section-header");

    //Varibles
    var endScroll,
      currPos = window.scrollY,
      screenHeight = window.innerHeight,
      stickyElementHeight = stickyElement.offsetHeight;

    let topGap = header.offsetHeight,
      bottomGap = 32;

    this.style.setProperty("--top-gap", topGap + `px`);

    stickyElement.style.top = topGap + `px`;

    function positionStickySidebar() {
      endScroll = window.innerHeight - stickyElement.offsetHeight - bottomGap;
      let stickyElementTop = parseInt(stickyElement.style.top.replace(`px;`, ``));
      let stickyElementNewTop;
      if (stickyElementHeight + topGap + bottomGap > screenHeight) {
        let scrollDelta = currPos - window.scrollY;
        if (scrollDelta > 0) {
          stickyElementNewTop = Math.min(stickyElementTop + scrollDelta, topGap);
        } else {
          stickyElementNewTop = Math.max(stickyElementTop + scrollDelta, endScroll);
        }
      } else {
        stickyElementNewTop = topGap;
      }
      stickyElement.style.top = stickyElementNewTop + `px`;

      currPos = window.scrollY;
    }

    function updateSticky() {
      screenHeight = window.innerHeight;
      stickyElementHeight = stickyElement.offsetHeight;
      positionStickySidebar();
    }

    //Follow the main function when scrolling

    const handleResize = () => {
      currPos = window.scrollY;
      updateSticky();
    };

    function attachEventListeners() {
      window.addEventListener("resize", handleResize);
      document.addEventListener(`scroll`, updateSticky, {
        capture: true,
        passive: true,
      });
    }

    if (window.innerWidth >= 1024) {
      attachEventListeners();
    }

    window.matchMedia(`(min-width: 1024px)`).addEventListener("change", () => {
      if (window.innerWidth >= 1024) {
        attachEventListeners();
      } else {
        window.removeEventListener("resize", handleResize);
        document.removeEventListener(`scroll`, updateSticky, {
          capture: true,
          passive: true,
        });
      }
    });
  }

  _attachEventListeners() {
    this.productForm.addEventListener("submit", this._onFormSubmit.bind(this));
    this.optionRadios.forEach((radio) => {
      radio.addEventListener("change", this._onOptionChange.bind(this));
    });
  }

  _onOptionChange(e) {
    const optionIndex = +e.target.dataset.optionPosition - 1;
    const newOptions = [...this.selectedVariant.options];
    newOptions[optionIndex] = e.target.value;

    for (let [id, variant] of Object.entries(this.productJson.variants)) {
      if (variant.options.join(",") === newOptions.join(",")) {
        this.selectedVariantId = id;
        return;
      }
    }
  }

  async _onFormSubmit(e) {
    e.preventDefault();

    const properties = {
      Auteur: this.productForm["properties[Auteur]"].value,
      _collectionTitle: this.productForm["properties[_collectionTitle]"].value,
      _collectionId: this.productForm["properties[_collectionId]"].value,
    };

    try {
      document.dispatchEvent(new CustomEvent("theme:loading:start"));

      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [{ id: this.selectedVariantId, quantity: this.productForm["quantity"].value, properties: properties }],
          sections: window.jollyVariables.cartSectionId
        }),
      });

      if(response.ok) {
        const json = await response.json();
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = json.sections[window.jollyVariables.cartSectionId];
        window.dataLayer.push({
          cloud_retail: "",
        });
        window.dataLayer.push(this.atcEventData);
        window.jollyFunctions.klaviyoTrackAddToCart(this.klaviyoAtcEventData);
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:rerender", {
            detail: {
              sectionEl: tempDiv.children[0],
              cart: { item_count: json.item_count, items: json.items },
              scrollToTop: true,
              new_items_ids: [this.variantId]
            },
          })
        );
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }
  }

  get selectedVariantId() {
    return this.productForm["variant-id"].value;
  }

  set selectedVariantId(value) {
    this.productForm["variant-id"].value = value;
    this.updateUrl();
    this.updatePrices();
    this.updateLowInStock();
    this.updateDeliveryInfo();
    document.dispatchEvent(new CustomEvent("variant:changed", { detail: { variant: this.selectedVariant } }));
  }

  updateUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", this.selectedVariantId);
    window.history.replaceState(null, "", url.toString());
  }

  updatePrices() {
    if(this.selectedVariant.available) {
      this.priceBlockEl.setAttribute('aria-hidden', false);
      this.atcBtnEl.setAttribute('aria-hidden', false);
      this.bisTriggerContainer.setAttribute('aria-hidden', true);

      const price = this.selectedVariant.price;
      const compareAtPrice = this.selectedVariant.compare_at_price;

      this.priceEl.innerHTML = formatMoney(price);
      if(compareAtPrice > price) {
        this.compareAtPriceBlockEl.setAttribute("aria-hidden", false);
        this.compareAtPriceEl.innerHTML = formatMoney(compareAtPrice);
        this.discountPercentageEl.innerHTML = Math.round((compareAtPrice - price) / compareAtPrice * 100);
      } else {
        this.compareAtPriceBlockEl.setAttribute("aria-hidden", true);
      }
    } else {
      this.priceBlockEl.setAttribute('aria-hidden', true);
      this.atcBtnEl.setAttribute('aria-hidden', true);
      this.bisTriggerContainer.setAttribute('aria-hidden', false);
    }
  }

  updateLowInStock() {
    this.visibleLowInStockEl?.setAttribute("aria-hidden", true);
    this.getLowInStockEl(this.selectedVariantId)?.setAttribute("aria-hidden", false);
  }

  updateDeliveryInfo() {
    if(this.selectedVariant.is_new) {
      this.deliveryInfoNewEl.setAttribute("aria-hidden", false);
      this.deliveryInfoUsedEl.setAttribute("aria-hidden", true);
    } else {
      this.deliveryInfoNewEl.setAttribute("aria-hidden", true);
      this.deliveryInfoUsedEl.setAttribute("aria-hidden", false);
    }
  }

  get deliveryInfoEl() {
    if(!this._deliveryInfoEl) {
      this._deliveryInfoEl = this.querySelector(".js-delivery-info");
    }
    return this._deliveryInfoEl;
  };
  get deliveryInfoNewEl() {
    if(!this._deliveryInfoNewEl) {
      this._deliveryInfoNewEl = this.deliveryInfoEl.querySelector(".js-new");
    }
    return this._deliveryInfoNewEl;
  }

  get deliveryInfoUsedEl() {
    if(!this._deliveryInfoUsedEl) {
      this._deliveryInfoUsedEl = this.deliveryInfoEl.querySelector(".js-used");
    }
    return this._deliveryInfoUsedEl;
  }

  get visibleLowInStockEl() {
    if(!this._lowInStockEls) {
      this._lowInStockEls = Array.from(this.querySelectorAll("[data-low-in-stock]"));
    }

    return this._lowInStockEls.find((el) => el.getAttribute('aria-hidden') === "false");
  }

  getLowInStockEl(variantId) {
    if(!this._lowInStockEls) {
      this._lowInStockEls = Array.from(this.querySelectorAll("[data-low-in-stock]"));
    }
    
    return this._lowInStockEls.find((el) => el.dataset.lowInStock == variantId);
  }

  get bisTriggerContainer() {
    if (!this._bisTriggerContainer) {
      this._bisTriggerContainer = this.querySelector(".js-bis-trigger-container");
    }
    return this._bisTriggerContainer;
  }

  get atcBtnEl() {
    if (!this._atcBtnEl) {
      this._atcBtnEl = this.querySelector(".js-atc-btn");
    }
    return this._atcBtnEl;
  }

  get priceBlockEl() {
    if (!this._priceBlockEl) {
      this._priceBlockEl = this.querySelector(".js-price-block");
    }
    return this._priceBlockEl;
  }

  get priceEl() {
    if (!this._priceEl) {
      this._priceEl = this.querySelector(".js-price");
    }
    return this._priceEl;
  }

  get compareAtPriceBlockEl() {
    if (!this._compareAtPriceBlockEl) {
      this._compareAtPriceBlockEl = this.priceBlockEl.querySelector(".js-compare-at-price-block");
    }
    return this._compareAtPriceBlockEl;
  }

  get compareAtPriceEl() {
    if (!this._compareAtPriceEl) {
      this._compareAtPriceEl = this.compareAtPriceBlockEl.querySelector(".js-compare-at-price");
    }
    return this._compareAtPriceEl;
  }

  get discountPercentageEl() {
    if (!this._discountPercentageEl) {
      this._discountPercentageEl =
        this.compareAtPriceBlockEl.querySelector(".js-discount-percentage");
    }
    return this._discountPercentageEl;
  }

  get selectedVariant() {
    return this.productJson.variants[this.selectedVariantId];
  }

  get atcEventData() {
    if (!this._initialAtcData) {
      this._initialAtcData = JSON.parse(this.querySelector(".js-atc-event-data").innerHTML);
    }
    const newAtcEventData = { ...this._initialAtcData };
    newAtcEventData.cloud_retail.visitorId = localStorage.getItem("visitorId");
    newAtcEventData.cloud_retail.productDetails = [
      {
        product: {
          id: this.selectedVariant.barcode,
        },
        quantity: 1
      },
    ];
    newAtcEventData.cloud_retail.cartId = window.cartToken;
    newAtcEventData.cloud_retail.userInfo = { userId: window.userId };
    newAtcEventData.productDetails[0].product.id = this.selectedVariant.barcode;

    return newAtcEventData;
  }

  get klaviyoAtcEventData() {
    if(!this._initialKlaviyoAtcEventData) {
      this._initialKlaviyoAtcEventData = JSON.parse(this.querySelector(".js-klaviyo-atc-event-data").innerHTML);
    }

    const _klaviyoAtcEventData = {...this._initialKlaviyoAtcEventData};
    _klaviyoAtcEventData.Price = formatMoney(this.selectedVariant.price);

    return _klaviyoAtcEventData;
  }

  get productJson() {
    if (!this._productJson) {
      this._productJson = JSON.parse(this.querySelector(".js-product-json").innerHTML);
    }
    return this._productJson;
  }

  get settings() {
    if (!this._settings) {
      this._settings = JSON.parse(this.dataset.sectionSettings);
    }
    return this._settings;
  }
}

customElements.define("product-section", ProductSection);
